import {
  FLOW_MODE,
  PENNYLANE_INVOICE_STATUS,
  RUN_STATUS,
  RUN_TYPE_LABELS,
  formatParisDate,
  formatParisDateTime,
} from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { Page, PageHeader, PageSection } from '@/components/layout/page.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { ErrorState } from '@/components/ui/error-state.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchPennylaneRunDetail } from './api.js';
import {
  RUNS_BACK_LINK,
  RunCount,
  RunDetailSkeleton,
  RunTimingItems,
} from './run-detail-layout.js';

export interface PennylaneRunDetailProps {
  runId: string;
}

const TYPE_LABEL = RUN_TYPE_LABELS.pennylane;

/**
 * `02` §11 : détail d'un run Pennylane — factures dont la date tombe dans sa
 * fenêtre. Affichage informatif ; les actions (Renvoyer / Forcer / Ignorer)
 * vivent sur la ligne ou la fiche de chaque facture, dans Commandes.
 */
export function PennylaneRunDetailPage({ runId }: PennylaneRunDetailProps): JSX.Element {
  const query = useQuery({
    queryKey: queryKeys.runs.pennylaneDetail(runId),
    queryFn: () => fetchPennylaneRunDetail(runId),
  });

  if (query.isPending) {
    return (
      <Page width="detail">
        <RunDetailSkeleton tables={1} />
      </Page>
    );
  }

  const errorState = query.isError ? (
    <ErrorState
      message={
        query.error instanceof ApiError ? query.error.message : 'Impossible de charger ce run.'
      }
      detail={query.error instanceof ApiError ? query.error.detail : undefined}
      onRetry={() => {
        void query.refetch();
      }}
    />
  ) : null;

  const run = query.data;
  // Sans données, l'erreur occupe la page ; un rechargement en échec (retour
  // sur l'onglet) laisse en revanche le run déjà chargé visible sous l'erreur.
  if (run === undefined) {
    return (
      <Page width="detail">
        <PageHeader backLink={RUNS_BACK_LINK} title={TYPE_LABEL} />
        {errorState}
      </Page>
    );
  }

  return (
    <Page width="detail">
      <PageHeader
        backLink={RUNS_BACK_LINK}
        title={`Run du ${formatParisDateTime(new Date(run.startedAt))}`}
        status={
          <>
            <StatusBadge registry={FLOW_MODE} code={run.simulation ? 'simulation' : 'active'} />
            <StatusBadge registry={RUN_STATUS} code={run.status} />
          </>
        }
        description={TYPE_LABEL}
      />

      {errorState}

      <PageSection>
        <DescriptionList>
          <RunTimingItems
            startedAt={run.startedAt}
            finishedAt={run.finishedAt}
            trigger={run.trigger}
          />
          <DescriptionItem label="Factures">
            <RunCount value={run.invoices.length} />
          </DescriptionItem>
          <DescriptionItem label="Erreurs">
            <RunCount value={run.stats.errors} alert />
          </DescriptionItem>
          {run.error ? (
            <DescriptionItem label="Erreur">
              <span className="text-status-red-fg">{run.error}</span>
            </DescriptionItem>
          ) : null}
        </DescriptionList>
      </PageSection>

      <PageSection title="Factures" flush>
        {run.invoices.length === 0 ? (
          <EmptyState
            title="Aucune facture"
            description="Aucune facture ne tombait dans la fenêtre de ce run."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Motif / erreur</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {run.invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell className="whitespace-nowrap font-medium tabular-nums">
                    {invoice.invoiceNumber}
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {formatParisDate(new Date(invoice.date))}
                  </TableCell>
                  <TableCell>{invoice.customerName ?? '—'}</TableCell>
                  <TableCell className="whitespace-nowrap text-right tabular-nums">
                    {invoice.amount ?? '—'}
                  </TableCell>
                  <TableCell>
                    <StatusBadge registry={PENNYLANE_INVOICE_STATUS} code={invoice.status} />
                  </TableCell>
                  <TableCell className="min-w-48">
                    {invoice.reason ?? invoice.lastError ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PageSection>
    </Page>
  );
}
